import {
  getCompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';

import {
  WbCard,
  WbCardContent,
  WbUnstyledLink,
} from '@agilelab/plugin-wb-platform';
import { WITBOOST_RELATION_PAIRS } from '@agilelab/plugin-wb-practice-shaper-common';
import { InfoCardVariants } from '@mainstage/core-components';
import { useAnalytics, useRouteRef } from '@mainstage/core-plugin-api';
import {
  Direction,
  EntityNode,
  EntityRelationsGraph,
  RelationPairs,
  catalogGraphRouteRef,
} from '@mainstage/plugin-catalog-graph';
import {
  entityRouteRef,
  humanizeEntityRef,
  useEntity,
} from '@mainstage/plugin-catalog-react';
import { IconButton, Theme, makeStyles } from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import qs from 'qs';
import { MouseEvent, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CatalogGraphRenderNode } from './CatalogGraphRenderNode';

const useStyles = makeStyles<Theme, { height: number | undefined }>(
  {
    card: () => ({
      height: '100%',
    }),
    graph: {
      flex: 1,
      height: '100%',
      minHeight: 0,
    },
  },
  { name: 'PluginCatalogGraphCatalogGraphCard' },
);

/**
 * Props for {@link EntityCatalogGraphCard }
 *
 * @public
 */
export interface CatalogGraphCardProps {
  variant?: InfoCardVariants;
  height?: number;
  title?: string;
  relationPairs?: RelationPairs;
  maxDepth?: number;
  unidirectional?: boolean;
  mergeRelations?: boolean;
  kinds?: string[];
  relations?: string[];
  direction?: Direction;

  zoom?: 'enabled' | 'disabled' | 'enable-on-click';
}

/**
 * @deprecated Backstage relations component
 */
export function CatalogGraphCard(props: CatalogGraphCardProps) {
  const {
    relationPairs = WITBOOST_RELATION_PAIRS,
    maxDepth = 1,
    unidirectional = true,
    mergeRelations = true,
    kinds,
    relations,
    direction = Direction.LEFT_RIGHT,
    height = 400,
    title = 'Relations',
    zoom = 'enable-on-click',
  } = props;

  const { entity } = useEntity();
  const currentLocation = useLocation().pathname;
  const entityName = getCompoundEntityRef(entity);
  const routePrefix = currentLocation.startsWith('/platform-settings')
    ? '/platform-settings'
    : '';
  const catalogEntityRoute = useRouteRef(entityRouteRef);

  const catalogGraphRoute = useRouteRef(catalogGraphRouteRef);
  const navigate = useNavigate();
  const classes = useStyles({ height });
  const analytics = useAnalytics();

  const onNodeClick = useCallback(
    (node: EntityNode, _: MouseEvent<unknown>) => {
      const nodeEntityName = parseEntityRef(node.id);
      const path = catalogEntityRoute({
        kind: nodeEntityName.kind.toLocaleLowerCase('en-US'),
        namespace: nodeEntityName.namespace.toLocaleLowerCase('en-US'),
        name: nodeEntityName.name,
      });
      analytics.captureEvent(
        'click',
        node.title ?? humanizeEntityRef(nodeEntityName),
        { attributes: { to: path } },
      );
      navigate(`${routePrefix}${path}`);
    },
    [catalogEntityRoute, navigate, analytics, routePrefix],
  );
  const catalogGraphParams = qs.stringify(
    {
      rootEntityRefs: [stringifyEntityRef(entity)],
      maxDepth: maxDepth + 1,
      unidirectional,
      mergeRelations,
      kinds,
      relations,
      direction,
    },
    { arrayFormat: 'brackets', addQueryPrefix: true },
  );
  const catalogGraphUrl = `${catalogGraphRoute()}${catalogGraphParams}`;

  return (
    <>
      <WbCard
        title={title}
        cardClassName={classes.card}
        actions={
          <IconButton
            component={WbUnstyledLink}
            aria-label="View graph"
            title="View graph"
            to={catalogGraphUrl ?? '#'}
            size="small"
          >
            <AssessmentIcon />
          </IconButton>
        }
      >
        <WbCardContent style={{ height: '350px' }}>
          <EntityRelationsGraph
            rootEntityNames={entityName}
            maxDepth={maxDepth}
            unidirectional={unidirectional}
            mergeRelations={mergeRelations}
            kinds={kinds}
            relations={relations}
            direction={direction}
            onNodeClick={onNodeClick}
            renderNode={CatalogGraphRenderNode}
            className={classes.graph}
            relationPairs={relationPairs}
            zoom={zoom}
          />
        </WbCardContent>
      </WbCard>
    </>
  );
}
